/**
 * Organisation Settings — Units of Measure & Value Realisation Parameters
 *
 * Lets org-admins configure:
 *   • Currency symbol + code (drives all monetary displays)
 *   • Volume unit (bbl / m³ / kL / L)  — O&G product-loss and ROI screens
 *   • Mass-rate unit (kg/hr / lb/hr / tonne/day) — Plume & Leak Analyser
 *   • Area unit (ha / acres / m²) — AgroTech field and treatment screens
 *   • ROI parameters: crude price per unit, avg regulatory fine, survey cost
 *   • Emissions: CH₄ → CO₂e GWP-100 factor
 *
 * Changes are persisted to organizations.settings->'uom' via PATCH /api/organizations/:id/settings.
 * The useOrgSettings hook propagates them to every screen on next load.
 */
import React, { useState, useEffect } from 'react';
import { Save, RotateCcw, Info } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import { useAuthStore, DEFAULT_UOM, OrgUomSettings } from '../../store/authStore';
import { organizationsApi } from '../../services/api';

// ─── Currency presets ─────────────────────────────────────────────────────────

const CURRENCY_PRESETS = [
  { code: 'USD', symbol: '$',     label: 'US Dollar (USD)' },
  { code: 'EUR', symbol: '€',     label: 'Euro (EUR)' },
  { code: 'GBP', symbol: '£',     label: 'British Pound (GBP)' },
  { code: 'INR', symbol: '₹',     label: 'Indian Rupee (INR)' },
  { code: 'AUD', symbol: 'A$',    label: 'Australian Dollar (AUD)' },
  { code: 'CAD', symbol: 'C$',    label: 'Canadian Dollar (CAD)' },
  { code: 'SGD', symbol: 'S$',    label: 'Singapore Dollar (SGD)' },
  { code: 'AED', symbol: 'د.إ',   label: 'UAE Dirham (AED)' },
  { code: 'BRL', symbol: 'R$',    label: 'Brazilian Real (BRL)' },
  { code: 'NOK', symbol: 'kr',    label: 'Norwegian Krone (NOK)' },
  { code: 'SAR', symbol: '﷼',     label: 'Saudi Riyal (SAR)' },
];

// ─── Volume unit presets ──────────────────────────────────────────────────────

const VOLUME_UNITS: { value: OrgUomSettings['volume_unit']; label: string }[] = [
  { value: 'bbl',  label: 'Barrels (bbl)' },
  { value: 'm3',   label: 'Cubic Metres (m³)' },
  { value: 'kL',   label: 'Kilolitres (kL)' },
  { value: 'L',    label: 'Litres (L)' },
];

// ─── Mass rate unit presets ───────────────────────────────────────────────────

const MASS_RATE_UNITS: { value: OrgUomSettings['mass_rate_unit']; label: string }[] = [
  { value: 'kg/hr',      label: 'Kilograms per hour (kg/hr)' },
  { value: 'lb/hr',      label: 'Pounds per hour (lb/hr)' },
  { value: 'tonne/day',  label: 'Tonnes per day (t/day)' },
];

// ─── Area unit presets ────────────────────────────────────────────────────────

const AREA_UNITS: { value: OrgUomSettings['area_unit']; label: string }[] = [
  { value: 'ha',    label: 'Hectares (ha)' },
  { value: 'acres', label: 'Acres' },
  { value: 'm2',    label: 'Square Metres (m²)' },
];

// ─── Input component ──────────────────────────────────────────────────────────

function Field({
  label, hint, children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      {children}
      {hint && (
        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500 flex items-start gap-1">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />{hint}
        </p>
      )}
    </div>
  );
}

const INPUT_CLS =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none';

const SELECT_CLS = INPUT_CLS;

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function UnitsOfMeasureSettingsPage() {
  const { currentOrganizationId, orgUomSettings, setOrgUomSettings } = useAuthStore();

  const [form, setForm] = useState<OrgUomSettings>({ ...DEFAULT_UOM });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // ── Load current settings ────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      if (!currentOrganizationId) return;
      setLoading(true);
      try {
        const res = await organizationsApi.getMyOrg(currentOrganizationId);
        const org = res.data ?? res;
        const saved: Partial<OrgUomSettings> = org?.settings?.uom ?? {};
        const currencyCode: string = org?.base_currency ?? 'USD';
        const currencyPreset = CURRENCY_PRESETS.find(c => c.code === currencyCode);
        setForm({
          ...DEFAULT_UOM,
          currency_code: currencyCode,
          currency_symbol: currencyPreset?.symbol ?? DEFAULT_UOM.currency_symbol,
          ...saved,
        });
      } catch {
        // Silently fall back to store / defaults
        setForm({ ...orgUomSettings });
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganizationId]);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const setField = <K extends keyof OrgUomSettings>(key: K, value: OrgUomSettings[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleCurrencyChange = (code: string) => {
    const preset = CURRENCY_PRESETS.find(c => c.code === code);
    setForm(prev => ({
      ...prev,
      currency_code: code,
      currency_symbol: preset?.symbol ?? prev.currency_symbol,
    }));
  };

  const handleVolumeUnitChange = (v: OrgUomSettings['volume_unit']) => {
    const labelMap: Record<OrgUomSettings['volume_unit'], string> = {
      bbl: 'bbl', m3: 'm³', kL: 'kL', L: 'L',
    };
    setForm(prev => ({ ...prev, volume_unit: v, volume_unit_label: labelMap[v] }));
  };

  const handleMassRateChange = (v: OrgUomSettings['mass_rate_unit']) => {
    const labelMap: Record<OrgUomSettings['mass_rate_unit'], string> = {
      'kg/hr': 'kg/hr', 'lb/hr': 'lb/hr', 'tonne/day': 't/day',
    };
    setForm(prev => ({ ...prev, mass_rate_unit: v, mass_rate_unit_label: labelMap[v] }));
  };

  const handleAreaChange = (v: OrgUomSettings['area_unit']) => {
    const labelMap: Record<OrgUomSettings['area_unit'], string> = {
      ha: 'ha', acres: 'acres', m2: 'm²',
    };
    setForm(prev => ({ ...prev, area_unit: v, area_unit_label: labelMap[v] }));
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!currentOrganizationId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await organizationsApi.updateSettings(currentOrganizationId, { uom: form, base_currency: form.currency_code });
      setOrgUomSettings(form);
      setSuccess('Settings saved — all screens will use the updated units immediately.');
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.response?.data?.message ?? 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm({ ...DEFAULT_UOM });
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Spinner size="lg" /></div>;
  }

  return (
    <div className="space-y-6 max-w-3xl">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Units of Measure &amp; Value Realisation
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Configure currency, volume, mass-rate, and area units for your jurisdiction.
          Changes apply immediately to all O&amp;G and AgroTech screens.
        </p>
      </div>

      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Currency ── */}
      <Card>
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-4">
          Currency
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Currency" hint="Used on all monetary displays across O&G and AgroTech screens">
            <select
              value={form.currency_code}
              onChange={e => handleCurrencyChange(e.target.value)}
              className={SELECT_CLS}
            >
              {CURRENCY_PRESETS.map(c => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
              <option value="custom">Other (set symbol manually)</option>
            </select>
          </Field>
          <Field label="Currency Symbol" hint="Overrides the symbol auto-selected from currency code">
            <input
              type="text"
              maxLength={5}
              value={form.currency_symbol}
              onChange={e => setField('currency_symbol', e.target.value)}
              className={INPUT_CLS}
              placeholder="$"
            />
          </Field>
        </div>
      </Card>

      {/* ── O&G Units ── */}
      <Card>
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-4">
          Oil &amp; Gas Units
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Volume Unit"
            hint="Applies to product-loss figures in Incident Closure, Regulatory Reporting, and Value Realisation"
          >
            <select
              value={form.volume_unit}
              onChange={e => handleVolumeUnitChange(e.target.value as OrgUomSettings['volume_unit'])}
              className={SELECT_CLS}
            >
              {VOLUME_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </Field>
          <Field
            label="Mass Rate Unit"
            hint="Applies to CH₄ plume rates in Plume & Leak Analyser, Analysis Review, and Response Planning"
          >
            <select
              value={form.mass_rate_unit}
              onChange={e => handleMassRateChange(e.target.value as OrgUomSettings['mass_rate_unit'])}
              className={SELECT_CLS}
            >
              {MASS_RATE_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </Field>
        </div>
      </Card>

      {/* ── AgroTech Units ── */}
      <Card>
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-4">
          AgroTech Units
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Area Unit"
            hint="Applies to field area, anomaly area, and treatment zone size across all AgroTech screens"
          >
            <select
              value={form.area_unit}
              onChange={e => handleAreaChange(e.target.value as OrgUomSettings['area_unit'])}
              className={SELECT_CLS}
            >
              {AREA_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </Field>
        </div>
      </Card>

      {/* ── Value Realisation Parameters ── */}
      <Card>
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1">
          Value Realisation Parameters
        </h2>
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
          These figures are used to compute intercepted product value, regulatory fine avoidance,
          and ROI on the Value Realisation screen. Set them to match your jurisdiction and commodity price.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field
            label={`Commodity Price (${form.currency_symbol} / ${form.volume_unit_label})`}
            hint="Current crude oil or condensate price used for intercepted product value calculation"
          >
            <input
              type="number"
              min={0}
              step={0.01}
              value={form.crude_price_per_unit}
              onChange={e => setField('crude_price_per_unit', parseFloat(e.target.value) || 0)}
              className={INPUT_CLS}
            />
          </Field>
          <Field
            label={`Avg Regulatory Fine (${form.currency_symbol})`}
            hint="Estimated average regulatory fine per incident — used to compute fine avoidance ROI"
          >
            <input
              type="number"
              min={0}
              step={1000}
              value={form.fine_per_incident}
              onChange={e => setField('fine_per_incident', parseFloat(e.target.value) || 0)}
              className={INPUT_CLS}
            />
          </Field>
          <Field
            label={`Survey Cost per Mission (${form.currency_symbol})`}
            hint="All-in estimated cost per OGI drone survey — used as denominator in ROI multiple"
          >
            <input
              type="number"
              min={0}
              step={100}
              value={form.survey_cost_per_mission}
              onChange={e => setField('survey_cost_per_mission', parseFloat(e.target.value) || 0)}
              className={INPUT_CLS}
            />
          </Field>
        </div>
      </Card>

      {/* ── Emissions ── */}
      <Card>
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1">
          Emissions Factor
        </h2>
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
          GWP-100 factor used to convert CH₄ mass to CO₂-equivalent on the Value Realisation screen.
          IPCC AR6 default is 29.8. Some jurisdictions use AR5 (28) or sector-specific values.
        </p>
        <div className="max-w-xs">
          <Field
            label="CH₄ → CO₂e Factor (kg CO₂e per kg CH₄)"
            hint="IPCC AR6 GWP-100 = 29.8 · AR5 GWP-100 = 28"
          >
            <input
              type="number"
              min={1}
              step={0.1}
              value={form.ch4_to_co2e_factor}
              onChange={e => setField('ch4_to_co2e_factor', parseFloat(e.target.value) || 29.8)}
              className={INPUT_CLS}
            />
          </Field>
        </div>
      </Card>

      {/* ── Preview ── */}
      <Card>
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">
          Preview
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-2 text-sm">
          {[
            ['Currency',          `${form.currency_symbol} (${form.currency_code})`],
            ['Volume',            form.volume_unit_label],
            ['Mass Rate',         form.mass_rate_unit_label],
            ['Area',              form.area_unit_label],
            ['Commodity Price',   `${form.currency_symbol}${form.crude_price_per_unit} / ${form.volume_unit_label}`],
            ['Avg Fine',          `${form.currency_symbol}${form.fine_per_incident.toLocaleString()}`],
            ['Survey Cost',       `${form.currency_symbol}${form.survey_cost_per_mission.toLocaleString()}`],
            ['GWP Factor',        `${form.ch4_to_co2e_factor} kg CO₂e/kg CH₄`],
          ].map(([k, v]) => (
            <div key={k}>
              <span className="text-xs text-gray-400 uppercase tracking-wide">{k}</span>
              <p className="font-semibold text-gray-800 dark:text-gray-200 mt-0.5">{v}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* ── Actions ── */}
      <div className="flex items-center gap-3">
        <Button onClick={handleSave} loading={saving} disabled={saving}>
          <Save className="w-4 h-4 mr-1.5" />Save Settings
        </Button>
        <Button variant="outline" onClick={handleReset} disabled={saving}>
          <RotateCcw className="w-4 h-4 mr-1.5" />Reset to Defaults
        </Button>
      </div>
    </div>
  );
}
